import React, { useEffect, useState } from 'react';
import { Calculator, Clock, DollarSign, TrendingUp } from 'lucide-react';
export function ROICalculatorSection() {
  const [minutesSaved, setMinutesSaved] = useState(30);
  const [jobsPerWeek, setJobsPerWeek] = useState(5);
  const [hourlyRate, setHourlyRate] = useState(75);
  const [weeklyTimeSaved, setWeeklyTimeSaved] = useState(0);
  const [weeklyMoneySaved, setWeeklyMoneySaved] = useState(0);
  const [weeksToPayoff, setWeeksToPayoff] = useState(0);
  const KIT_PRICE = 749.99;
  useEffect(() => {
    // Calculate weekly time saved in hours
    const timeSavedHours = minutesSaved * jobsPerWeek / 60;
    setWeeklyTimeSaved(parseFloat(timeSavedHours.toFixed(1)));
    // Calculate weekly money saved
    const moneySaved = timeSavedHours * hourlyRate;
    setWeeklyMoneySaved(Math.round(moneySaved));
    // Calculate weeks to payoff
    const payoff = moneySaved > 0 ? KIT_PRICE / moneySaved : 0;
    setWeeksToPayoff(parseFloat(payoff.toFixed(1)));
  }, [minutesSaved, jobsPerWeek, hourlyRate]);
  return <section className="py-16 bg-brand-light" data-id="element-536">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-537">
        <div className="text-center mb-8" data-id="element-538">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-2" data-id="element-539">
            How Many Jobs Until It Pays for Itself?
          </h2>
          <p className="text-lg text-gray-600" data-id="element-540">
            See the ROI for your business.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 max-w-5xl mx-auto" data-id="element-541">
          <div className="grid grid-cols-1 lg:grid-cols-5" data-id="element-542">
            {/* Controls */}
            <div className="lg:col-span-3 p-6 md:p-8 space-y-5 bg-white" data-id="element-543">
              <div data-id="element-544">
                <div className="flex justify-between mb-1.5" data-id="element-545">
                  <label className="font-bold text-brand-dark text-sm flex items-center" data-id="element-546">
                    <Clock className="w-4 h-4 mr-2 text-gray-400" data-id="element-547" />
                    Minutes Saved Per Job
                  </label>
                  <span className="font-bold text-brand-yellow" data-id="element-548">
                    {minutesSaved} min
                  </span>
                </div>
                <input type="range" min="10" max="60" step="5" value={minutesSaved} onChange={e => setMinutesSaved(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-yellow" data-id="element-549" />
                <div className="flex justify-between text-xs text-gray-400 mt-0.5" data-id="element-550">
                  <span data-id="element-551">10 min</span>
                  <span data-id="element-552">60 min</span>
                </div>
              </div>

              <div data-id="element-553">
                <div className="flex justify-between mb-1.5" data-id="element-554">
                  <label className="font-bold text-brand-dark text-sm flex items-center" data-id="element-555">
                    <TrendingUp className="w-4 h-4 mr-2 text-gray-400" data-id="element-556" />
                    Jobs Per Week
                  </label>
                  <span className="font-bold text-brand-yellow" data-id="element-557">
                    {jobsPerWeek} jobs
                  </span>
                </div>
                <input type="range" min="1" max="20" step="1" value={jobsPerWeek} onChange={e => setJobsPerWeek(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-yellow" data-id="element-558" />
                <div className="flex justify-between text-xs text-gray-400 mt-0.5" data-id="element-559">
                  <span data-id="element-560">1 job</span>
                  <span data-id="element-561">20 jobs</span>
                </div>
              </div>

              <div data-id="element-562">
                <div className="flex justify-between mb-1.5" data-id="element-563">
                  <label className="font-bold text-brand-dark text-sm flex items-center" data-id="element-564">
                    <DollarSign className="w-4 h-4 mr-2 text-gray-400" data-id="element-565" />
                    Hourly Labor Rate
                  </label>
                  <span className="font-bold text-brand-yellow" data-id="element-566">
                    ${hourlyRate}/hr
                  </span>
                </div>
                <input type="range" min="25" max="150" step="5" value={hourlyRate} onChange={e => setHourlyRate(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-yellow" data-id="element-567" />
                <div className="flex justify-between text-xs text-gray-400 mt-0.5" data-id="element-568">
                  <span data-id="element-569">$25/hr</span>
                  <span data-id="element-570">$150/hr</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-2 bg-brand-dark p-6 md:p-8 flex flex-col justify-center text-white relative overflow-hidden" data-id="element-571">
              <div className="absolute top-0 right-0 p-2 opacity-10" data-id="element-572">
                <Calculator className="w-32 h-32" data-id="element-573" />
              </div>

              <div className="relative z-10 space-y-5" data-id="element-574">
                <div className="flex gap-6" data-id="element-575">
                  <div data-id="element-576">
                    <div className="text-gray-400 text-xs uppercase tracking-wider font-bold mb-0.5" data-id="element-577">
                      Weekly Time Saved
                    </div>
                    <div className="text-2xl font-bold text-white" data-id="element-578">
                      {weeklyTimeSaved}{' '}
                      <span className="text-sm text-gray-400 font-normal" data-id="element-579">
                        hrs
                      </span>
                    </div>
                  </div>
                  <div data-id="element-580">
                    <div className="text-gray-400 text-xs uppercase tracking-wider font-bold mb-0.5" data-id="element-581">
                      Weekly Value
                    </div>
                    <div className="text-2xl font-bold text-brand-yellow" data-id="element-582">
                      ${weeklyMoneySaved}
                    </div>
                  </div>
                </div>

                <div className="pt-5 border-t border-gray-700" data-id="element-583">
                  <div className="text-gray-300 text-sm mb-1" data-id="element-584">
                    Pays for itself in:
                  </div>
                  <div className="text-4xl font-bold text-white" data-id="element-585">
                    {weeksToPayoff}{' '}
                    <span className="text-xl text-brand-yellow" data-id="element-586">weeks</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-1" data-id="element-587">
                    Based on Complete Kit at ${KIT_PRICE}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}